import { useEffect, useRef, useState } from 'react';
import { Viewer } from '@photo-sphere-viewer/core';
import { VirtualTourPlugin } from '@photo-sphere-viewer/virtual-tour-plugin';
import '@photo-sphere-viewer/core/index.css';
import '@photo-sphere-viewer/virtual-tour-plugin/index.css';

import panoramaV1 from '@/assets/girls-bar-360-v1.jpg';
import panoramaV2 from '@/assets/girls-bar-360-v2.jpg';

const SCENES = [
  { id: 'counter', label: 'カウンター席', panorama: panoramaV1 },
  { id: 'lounge', label: 'ソファ席', panorama: panoramaV2 },
] as const;

type SceneId = (typeof SCENES)[number]['id'];

export default function PanoramaTour() {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<Viewer | null>(null);
  const [activeScene, setActiveScene] = useState<SceneId>('counter');
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const viewer = new Viewer({
      container,
      loadingTxt: '読み込み中...',
      touchmoveTwoFingers: false,
      mousemove: true,
      mousewheel: true,
      mousewheelCtrlKey: false,
      defaultYaw: 0,
      defaultPitch: 0,
      navbar: ['zoom', 'move', 'fullscreen'],
      plugins: [
        VirtualTourPlugin.withConfig({
          renderMode: '3d',
          positionMode: 'manual',
          dataMode: 'client',
          preload: true,
          showLinkTooltip: true,
          transitionOptions: {
            showLoader: true,
            speed: '20rpm',
            effect: 'fade',
            rotation: true,
          },
          nodes: [
            {
              id: 'counter',
              name: 'カウンター席',
              panorama: panoramaV1,
              links: [
                {
                  nodeId: 'lounge',
                  position: { yaw: '1.35rad', pitch: '-0.12rad' },
                  name: 'ソファ席へ',
                },
              ],
            },
            {
              id: 'lounge',
              name: 'ソファ席',
              panorama: panoramaV2,
              links: [
                {
                  nodeId: 'counter',
                  position: { yaw: '-1.85rad', pitch: '-0.12rad' },
                  name: 'カウンター席へ',
                },
              ],
            },
          ],
        }),
      ],
    });

    viewerRef.current = viewer;

    const virtualTour = viewer.getPlugin(VirtualTourPlugin);
    virtualTour.addEventListener('node-changed', ({ node }) => {
      setActiveScene(node.id as SceneId);
    });

    viewer.addEventListener('ready', () => {
      setIsReady(true);
    });

    return () => {
      viewer.destroy();
      viewerRef.current = null;
      setIsReady(false);
    };
  }, []);

  const goToScene = (sceneId: SceneId) => {
    const viewer = viewerRef.current;
    if (!viewer || !isReady) return;

    const virtualTour = viewer.getPlugin(VirtualTourPlugin);
    virtualTour.setCurrentNode(sceneId);
  };

  return (
    <div>
      <div
        className="relative w-full overflow-hidden rounded-lg shadow-sm"
        style={{
          height: 'min(70vh, 520px)',
          border: '1px solid rgba(170,196,220,0.35)',
          touchAction: 'none',
        }}
      >
        <div ref={containerRef} className="w-full h-full" />
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
        {SCENES.map((scene) => (
          <button
            key={scene.id}
            type="button"
            onClick={() => goToScene(scene.id)}
            disabled={!isReady}
            className="px-4 py-2 text-xs tracking-widest rounded-full transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              fontFamily: "'Noto Serif JP', serif",
              background: activeScene === scene.id ? '#3d8a7a' : 'rgba(170,196,220,0.15)',
              color: activeScene === scene.id ? '#fff' : '#555',
              border: `1px solid ${activeScene === scene.id ? '#3d8a7a' : 'rgba(170,196,220,0.4)'}`,
            }}
          >
            {scene.label}
          </button>
        ))}
      </div>

      <p className="text-center text-xs text-gray-400 tracking-wider mt-3 leading-relaxed">
        指でドラッグして店内を360°見渡せます。
        <br className="sm:hidden" />
        矢印またはボタンで別のエリアへ移動できます。
      </p>
    </div>
  );
}
